import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CloudUpload,
  Download,
  CheckCircle,
  Error,
  Info,
  Close,
  ExpandMore,
  Check,
  PriorityHigh
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BulkEmployeeUpload = ({ seatStatus, refreshSeatStatus }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resultsDialog, setResultsDialog] = useState(false);
  const [templateStructure, setTemplateStructure] = useState(null);

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setError('');
      setUploadResult(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const response = await fetch(`${API_URL}/api/alldetails/download-template`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to download template');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_bulk_upload_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Load template structure
      loadTemplateStructure();

    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const loadTemplateStructure = async () => {
    try {
      const token = localStorage.getItem("token");
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const response = await fetch(`${API_URL}/api/alldetails/template-structure`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setTemplateStructure(result.data);
        }
      }
    } catch (err) {
      //console.error('Failed to load template structure:', err);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (seatStatus?.isExpired) {
      setError('Subscription/Trial has expired. Please upgrade your plan .');
      return;
    }

    if (seatStatus?.isLimitReached) {
      setError(`User seat limit reached (${seatStatus?.usedSeats}/${seatStatus?.totalSeats}). You cannot upload more employees under your current plan. Please upgrade your subscription `);
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem("token");
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/alldetails/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setUploadResult(result.data);
        setSuccess(result.message);
        setResultsDialog(true);
        setFile(null);
        if (refreshSeatStatus) refreshSeatStatus();
      } else {
        const errorMsg = result.message || 'Upload failed';
        setError(errorMsg);
        const errorData = result.data?.errors || result.errors;
        if (errorData && errorData.length > 0) {
          setUploadResult(result.data || {
            total: result.data?.total || errorData.length,
            success: 0,
            failed: errorData.length,
            errors: errorData,
            employees: []
          });
          setResultsDialog(true);
        }
        if (refreshSeatStatus) refreshSeatStatus();
      }
    } catch (err) {
      setError('Upload error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCloseResults = () => {
    setResultsDialog(false);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setUploadResult(null);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom  sx={{ mb: 3 }}>
          Bulk Employee Upload
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Instructions */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Instructions:
            </Typography>
            <Typography variant="body2" paragraph>
              1. Download the template file below
            </Typography>

            <Typography variant="body2" paragraph>
              2. <strong>employee_id</strong> and <strong>companyemail</strong> are auto-generated if not provided
            </Typography>
            <Typography variant="body2" paragraph>
              3. Upload the completed Excel file
            </Typography>
       
          </CardContent>
        </Card>

        {/* Field Instructions Accordion */}
        {/* <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" color="primary">
              📋 View All Template Fields & Instructions
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom color="error">
                  Required Fields (Must be filled):
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <PriorityHigh color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="candidatename" 
                      secondary="Full name of the employee (e.g., 'John Doe')" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PriorityHigh color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="personalemail" 
                      secondary="Personal email address (e.g., 'john.doe@example.com')" 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Auto-generated Fields (Leave empty):
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Check color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="employee_id" 
                      secondary="Auto-generated if not provided" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="companyemail" 
                      secondary="Auto-generated if not provided" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="firstName, lastName" 
                      secondary="Auto-extracted from candidatename" 
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Personal Information Fields (Optional as later it can be updated by employee):
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { field: 'fatherName', desc: "Father's name" },
                    { field: 'dateOfBirth', desc: 'YYYY-MM-DD format' },
                    { field: 'anniversary', desc: 'YYYY-MM-DD format' },
                    { field: 'gender', desc: 'Male/Female/Other' },
                    { field: 'panNumber', desc: 'PAN card number' },
                    { field: 'adharCardNumber', desc: 'Aadhar card number' },
                    { field: 'phoneNumber', desc: 'Primary phone number' }
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.field}>
                      <Chip 
                        label={item.field} 
                        size="small" 
                        variant="outlined"
                        sx={{ mb: 0.5 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        {item.desc}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Current Address Fields (Optional as later it can be updated by employee):
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { field: 'houseNumber', desc: 'House/Flat number' },
                    { field: 'street', desc: 'Street address' },
                    { field: 'streetName', desc: 'Cross street name' },
                    { field: 'area', desc: 'Area/Locality' },
                    { field: 'city', desc: 'City' },
                    { field: 'pinCode', desc: 'PIN code' },
                    { field: 'mobile', desc: 'Emergency mobile number' },
                    { field: 'landline', desc: 'Landline number' }
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={3} key={item.field}>
                      <Chip 
                        label={item.field} 
                        size="small" 
                        variant="outlined"
                        sx={{ mb: 0.5 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        {item.desc}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion> */}

        {/* Download Template Button */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadTemplate}
            sx={{ mr: 2 }}
          >
            Download Template
          </Button>
          
          <Tooltip title="Only candidatename and personalemail are required. All other fields are optional and will be auto-generated if needed.">
            <IconButton>
              <Info />
            </IconButton>
          </Tooltip>
        </Box>

        {/* File Upload Area */}
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.400',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            mb: 3,
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          {isDragActive ? (
            <Typography variant="h6">Drop the Excel file here...</Typography>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                Drag & drop an Excel file here, or click to select
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supports .xlsx and .xls files (Max 10MB)
              </Typography>
            </>
          )}
        </Box>

        {/* Selected File */}
        {file && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle1">
                    Selected File: {file.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                <IconButton onClick={handleRemoveFile} size="small">
                  <Close />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Upload Button */}
        <Button
          variant="contained"
          size="large"
          onClick={handleUpload}
          disabled={!file || uploading || seatStatus?.isLimitReached || seatStatus?.isExpired}
          sx={{ py: 1.5, ml: 'auto' }}
        >
          {uploading 
            ? 'Uploading...' 
            : (seatStatus?.isLimitReached 
                ? 'Seat Limit Reached' 
                : (seatStatus?.isExpired ? 'Trial Expired' : 'Upload Excel File'))}
        </Button>

        {/* Progress Bar */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Processing... Please wait
            </Typography>
          </Box>
        )}

        {/* Messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 3 }}>
            {success}
          </Alert>
        )}
      </Paper>

      {/* Results Dialog */}
      <Dialog
        open={resultsDialog}
        onClose={handleCloseResults}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {uploadResult?.failed > 0 && (!uploadResult?.success || uploadResult?.success === 0) ? (
              <>
                <Error sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" color="error.main">Bulk Upload Validation Errors</Typography>
              </>
            ) : (
              <>
                <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">Bulk Upload Results</Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {uploadResult && (
            <Box>
              {/* Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {uploadResult.total}
                      </Typography>
                      <Typography variant="body2">Total Records</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {uploadResult.success}
                      </Typography>
                      <Typography variant="body2">Successfully Added</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color={uploadResult.failed > 0 ? 'error' : 'text.secondary'}>
                        {uploadResult.failed}
                      </Typography>
                      <Typography variant="body2">Failed</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Successfully Added Employees */}
              {uploadResult.success > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Successfully Added Employees:
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Employee ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Personal Email</TableCell>
                          <TableCell>Company Email</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadResult.employees.map((emp) => (
                          <TableRow key={emp.row} hover>
                            <TableCell>{emp.row}</TableCell>
                            <TableCell>
                              <Chip label={emp.employee_id} size="small" color="primary" />
                            </TableCell>
                            <TableCell>{emp.name}</TableCell>
                            <TableCell>{emp.personalemail}</TableCell>
                            <TableCell>{emp.companyemail}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Errors */}
              {uploadResult.failed > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom color="error">
                    Errors ({uploadResult.failed}):
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Candidate Name</TableCell>
                          <TableCell>Personal Email</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadResult.errors.map((err, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{err.row}</TableCell>
                            <TableCell>{err.data?.candidatename || `${err.data?.first_name || ''} ${err.data?.last_name || ''}`.trim() || 'N/A'}</TableCell>
                            <TableCell>{err.data?.personalemail || err.data?.personal_email || 'N/A'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Error sx={{ color: 'error.main', mr: 1, mt: 0.5, fontSize: 16 }} />
                                <Typography variant="body2">{err.error}</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseResults} variant="contained" fullWidth>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BulkEmployeeUpload;